import { faro } from '@grafana/faro-react'
import { collectAppDetails } from 'services/zoomDiagnostics/collectAppDetails'
import { resetZoomDiagnosticsSessionContext } from 'services/zoomDiagnostics/zoomDiagnosticsSessionContext'
import type { ZoomClassEndTrigger } from 'constants/analyticsEvents'
import type { RemovalCause } from 'services/zoomDiagnostics/removalCause'

const JOIN_ATTEMPT_KEY_PREFIX = 'zoomJoinAttempt:'

export type { ZoomClassEndTrigger } from 'constants/analyticsEvents'

export interface ZoomNetworkErrorParams {
    connectionState: string
    reason?: string
    errorCode?: number | string
}

const getJoinAttemptStorageKey = (classId: string): string => `${JOIN_ATTEMPT_KEY_PREFIX}${classId}`

const incrementJoinAttempt = (classId: string): number => {
    if (typeof sessionStorage === 'undefined') {
        return 1
    }

    const key = getJoinAttemptStorageKey(classId || 'unknown')
    const current = parseInt(sessionStorage.getItem(key) ?? '0', 10)
    const next = current + 1
    sessionStorage.setItem(key, String(next))
    return next
}

const pushLifecycleEvent = (eventName: string, attributes: Record<string, string>): void => {
    if (!faro.api) {
        return
    }

    faro.api.pushEvent(eventName, attributes)
}

const getBaseAttributes = (): Record<string, string> => {
    const app = collectAppDetails()
    const attributes: Record<string, string> = {
        classId: app.classroomId,
        wseUserId: app.wseUserId,
        role: app.role,
        timestamp: new Date().toISOString()
    }

    if (app.zoomTopic) {
        attributes.zoomTopic = app.zoomTopic
    }

    return attributes
}

export const logZoomSessionJoined = (): void => {
    resetZoomDiagnosticsSessionContext()
    const app = collectAppDetails()
    const joinAttemptNumber = incrementJoinAttempt(app.classroomId)

    pushLifecycleEvent('zoom_session_joined', {
        ...getBaseAttributes(),
        joinAttemptNumber: String(joinAttemptNumber),
        isRejoin: String(joinAttemptNumber > 1)
    })
}

export const logZoomNetworkError = (params: ZoomNetworkErrorParams): void => {
    const attributes: Record<string, string> = {
        ...getBaseAttributes(),
        connectionState: params.connectionState
    }

    if (params.reason) {
        attributes.reason = params.reason
    }

    if (params.errorCode !== undefined) {
        attributes.errorCode = String(params.errorCode)
    }

    pushLifecycleEvent('zoom_network_error', attributes)
}

export const logZoomUserAdded = (participantUserId: string, participantUserName?: string): void => {
    const attributes: Record<string, string> = {
        ...getBaseAttributes(),
        participantUserId
    }

    if (participantUserName) {
        attributes.participantUserName = participantUserName
    }

    pushLifecycleEvent('zoom_user_added', attributes)
}

export const logZoomUserRemoved = (
    participantUserId: string,
    participantUserName?: string,
    removalCause?: RemovalCause
): void => {
    const attributes: Record<string, string> = {
        ...getBaseAttributes(),
        participantUserId
    }

    if (participantUserName) {
        attributes.participantUserName = participantUserName
    }

    if (removalCause) {
        attributes.removalCause = removalCause
    }

    pushLifecycleEvent('zoom_user_removed', attributes)
}

export interface ZoomParticipantDisconnectParams {
    removalCause: RemovalCause
    rawReason?: string
    connectionState?: string
}

export const logZoomParticipantDisconnect = (params: ZoomParticipantDisconnectParams): void => {
    const attributes: Record<string, string> = {
        ...getBaseAttributes(),
        removalCause: params.removalCause
    }

    if (params.connectionState) {
        attributes.connectionState = params.connectionState
    }

    if (params.rawReason !== undefined) {
        attributes.rawReason = params.rawReason
    }

    pushLifecycleEvent('zoom_participant_disconnect', attributes)
}

export const logZoomSessionClosed = (connectionState: string, reason?: string): void => {
    const attributes: Record<string, string> = {
        ...getBaseAttributes(),
        connectionState
    }

    if (reason) {
        attributes.reason = reason
    }

    pushLifecycleEvent('zoom_session_closed', attributes)
}

export const logZoomClassEnded = (trigger: ZoomClassEndTrigger): void => {
    pushLifecycleEvent('zoom_class_ended', {
        ...getBaseAttributes(),
        trigger
    })
}

export const logZoomStudentLeft = (): void => {
    pushLifecycleEvent('zoom_student_left', {
        ...getBaseAttributes()
    })
}

export const resetZoomLifecycleStateForTests = (): void => {
    if (typeof sessionStorage === 'undefined') {
        return
    }

    const keysToRemove: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key?.startsWith(JOIN_ATTEMPT_KEY_PREFIX)) {
            keysToRemove.push(key)
        }
    }

    keysToRemove.forEach(key => sessionStorage.removeItem(key))
}
